import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }
const TIER_COLOR = {
  essential: { bg: '#DBEAFE', fg: '#1E40AF' },
  premium:   { bg: '#FCE7F3', fg: '#9D174D' },
  comercial: { bg: '#D1FAE5', fg: '#065F46' },
}

export default function AdminKits() {
  const [kits, setKits] = useState([])
  const [loading, setLoading] = useState(true)
  const [generandoFoto, setGenerandoFoto] = useState(null) // kit.codigo en progreso
  const [preparandoLora, setPreparandoLora] = useState(null) // kit.codigo en progreso
  const [datasetResult, setDatasetResult] = useState(null) // resultado del último dataset preparado

  useEffect(() => { load() }, [])

  async function generarFotoKit(e, kitCodigo, kitNombre) {
    e.preventDefault()
    e.stopPropagation()
    if (generandoFoto) return
    if (!confirm(`Generar foto AI del kit "${kitNombre}"?\n\nEsto compone los productos del kit y los refina con Flux Kontext Pro. Tarda ~30-60 segundos y consume crédito Replicate.`)) return

    setGenerandoFoto(kitCodigo)
    try {
      const { data: session } = await supabase.auth.getSession()
      const jwt = session?.session?.access_token
      if (!jwt) throw new Error('Sin sesión activa')
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/procesar-foto-kit`
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kit_codigo: kitCodigo }),
      })
      const json = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(json.error || `Error ${r.status}`)
      alert(`✓ Foto generada para ${kitNombre}\n\nProductos usados: ${(json.productos_usados || []).join(', ')}\n\nURL: ${json.imagen_url}`)
      await load()
    } catch (err) {
      alert(`Error generando foto: ${err.message}`)
    } finally {
      setGenerandoFoto(null)
    }
  }

  async function prepararDatasetLora(e, kitCodigo, kitNombre, nItems) {
    e.preventDefault()
    e.stopPropagation()
    if (preparandoLora) return
    if (!confirm(`Preparar dataset LoRA para "${kitNombre}"?\n\nVa a:\n• Bajar las ${nItems} fotos de productos\n• Generar captions automáticos con Claude Vision (claude-3-5-sonnet)\n• Empaquetar todo en un ZIP\n\nTarda ~2-4 minutos. Usa ~$0.50 de Anthropic API.\n\nDespués subís el ZIP a Replicate UI para entrenar el LoRA.`)) return

    setPreparandoLora(kitCodigo)
    setDatasetResult(null)
    try {
      const { data: session } = await supabase.auth.getSession()
      const jwt = session?.session?.access_token
      if (!jwt) throw new Error('Sin sesión activa')
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/preparar-dataset-lora-kit`
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${jwt}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ kit_codigo: kitCodigo }),
      })
      const json = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(json.error || `Error ${r.status}`)
      setDatasetResult({ ...json, kitNombre })
    } catch (err) {
      alert(`Error preparando dataset: ${err.message}`)
    } finally {
      setPreparandoLora(null)
    }
  }

  async function load() {
    setLoading(true)
    // Kits + items + costo total
    const { data: kitsData, error } = await supabase
      .from('kits')
      .select(`
        codigo, nombre, tier, personas, precio_susc, precio_unica, costo_estimado, activo, orden, imagen_url,
        items:kit_items(cantidad, es_regalo, producto:productos(sku, nombre, costo_unitario, precio_publico, iva_pct, stock_actual))
      `)
      .order('tier').order('orden').order('nombre')

    if (error) { console.error(error); setLoading(false); return }

    // Regla c/IVA: tanto costo_unitario como precio_publico ya están c/IVA en DB.
    // NO multiplicar por (1+iva/100) — eso producía un bug histórico de doble IVA.
    const procesados = (kitsData || []).map(k => {
      const items = k.items || []
      const costoCIva = items.reduce((s, i) => {
        const cu = Number(i.producto?.costo_unitario || 0)
        return s + cu * Number(i.cantidad || 0)
      }, 0)
      // Suma de precios sueltos (excluye regalos)
      const precioSueltos = items.reduce((s, i) => {
        if (i.es_regalo) return s
        const pp = Number(i.producto?.precio_publico || 0)
        return s + pp * Number(i.cantidad || 0)
      }, 0)
      const sinStockSuficiente = items.filter(i => {
        const stock = Number(i.producto?.stock_actual || 0)
        return stock < Number(i.cantidad || 0)
      }).length
      return {
        ...k,
        imagen_url: k.imagen_url,
        nItems: items.length,
        costoCIva,
        precioSueltos,
        sinStockSuficiente,
      }
    })

    setKits(procesados)
    setLoading(false)
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>Kits</h1>
        <p className="lead">Editá la composición de cada kit · Limpack</p>

        {loading ? (
          <div className="card">Cargando…</div>
        ) : kits.length === 0 ? (
          <div className="card">No hay kits cargados.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {kits.map(k => {
              const color = TIER_COLOR[k.tier] || TIER_COLOR.essential
              const precioSusc = Number(k.precio_susc || 0)
              const precioUnica = Number(k.precio_unica || 0)
              const margenSusc = precioSusc - k.costoCIva
              const margenSuscPct = precioSusc > 0 ? (margenSusc / precioSusc) * 100 : 0
              const margenUnicaPct = precioUnica > 0 ? ((precioUnica - k.costoCIva) / precioUnica) * 100 : 0
              const ahorroSuscVsSueltosPct = k.precioSueltos > 0 ? ((k.precioSueltos - precioSusc) / k.precioSueltos) * 100 : 0
              const ahorroColor = ahorroSuscVsSueltosPct >= 15 ? '#059669' : ahorroSuscVsSueltosPct >= 10 ? '#D97706' : '#DC2626'
              return (
                <Link
                  key={k.codigo}
                  to={`/admin/kits/${k.codigo}`}
                  style={{
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    padding: 16,
                    textDecoration: 'none',
                    color: '#1A3461',
                    transition: 'transform .15s, box-shadow .15s',
                    display: 'block',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,31,61,.12)' }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ fontSize: 17, fontWeight: 800 }}>{k.nombre}</div>
                    <span style={{
                      background: color.bg, color: color.fg,
                      padding: '3px 10px', borderRadius: 999,
                      fontSize: 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase',
                    }}>{TIER_LABEL[k.tier] || k.tier}</span>
                  </div>
                  {k.personas && (
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>{k.personas}</div>
                  )}
                  {/* 3 PRECIOS: Susc · Única · Sueltos */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
                    <div style={{ background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: 8, padding: '6px 8px' }}>
                      <div style={{ fontSize: 9, color: '#047857', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 800 }}>Susc/mes</div>
                      <div style={{ fontSize: 16, fontWeight: 900, color: '#065F46' }}>${precioSusc.toLocaleString('es-UY')}</div>
                    </div>
                    <div style={{ background: '#EFF6FF', border: '1px solid #93C5FD', borderRadius: 8, padding: '6px 8px' }}>
                      <div style={{ fontSize: 9, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 800 }}>Única</div>
                      <div style={{ fontSize: 16, fontWeight: 900, color: '#1E3A8A' }}>${precioUnica.toLocaleString('es-UY')}</div>
                    </div>
                    <div style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: 8, padding: '6px 8px' }}>
                      <div style={{ fontSize: 9, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 800 }}>Sueltos</div>
                      <div style={{ fontSize: 16, fontWeight: 900, color: '#475569' }}>${Math.round(k.precioSueltos).toLocaleString('es-UY')}</div>
                    </div>
                  </div>
                  {/* Costo + ahorro vs sueltos */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>Costo c/IVA</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#475569' }}>${Math.round(k.costoCIva).toLocaleString('es-UY')}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>Ahorro susc</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: ahorroColor }}>−{ahorroSuscVsSueltosPct.toFixed(0)}%</div>
                    </div>
                  </div>
                  {/* Margen + ítems */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                    <span style={{ color: '#64748b' }}>{k.nItems} ítem{k.nItems !== 1 ? 's' : ''}</span>
                    <span style={{ color: '#64748b' }}>
                      Margen <strong style={{ color: margenSuscPct > 50 ? '#059669' : margenSuscPct > 35 ? '#D97706' : '#DC2626' }}>{margenSuscPct.toFixed(0)}%</strong> susc · <strong style={{ color: margenUnicaPct > 50 ? '#059669' : margenUnicaPct > 35 ? '#D97706' : '#DC2626' }}>{margenUnicaPct.toFixed(0)}%</strong> única
                    </span>
                  </div>
                  {k.sinStockSuficiente > 0 && (
                    <div style={{
                      marginTop: 10, padding: '6px 10px',
                      background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 8,
                      fontSize: 11, color: '#7F1D1D', fontWeight: 700,
                    }}>
                      ⚠ {k.sinStockSuficiente} producto{k.sinStockSuficiente !== 1 ? 's' : ''} con stock insuficiente
                    </div>
                  )}
                  {/* Foto del kit + botón generar */}
                  <div style={{
                    marginTop: 12, paddingTop: 10, borderTop: '1px solid #f1f5f9',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    {k.imagen_url ? (
                      <a
                        href={k.imagen_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{
                          width: 52, height: 52, borderRadius: 8,
                          background: `#F8FAFC url(${k.imagen_url}) center/cover`,
                          border: '1px solid #e2e8f0',
                          flexShrink: 0,
                        }}
                        title="Ver foto del kit en grande"
                      />
                    ) : (
                      <div style={{
                        width: 52, height: 52, borderRadius: 8,
                        background: '#F1F5F9', border: '1px dashed #cbd5e1',
                        display: 'grid', placeItems: 'center',
                        fontSize: 11, color: '#94a3b8',
                        flexShrink: 0,
                      }}>
                        sin foto
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={e => generarFotoKit(e, k.codigo, k.nombre)}
                      disabled={generandoFoto === k.codigo}
                      style={{
                        flex: 1,
                        background: generandoFoto === k.codigo ? '#94a3b8' : '#1A3461',
                        color: '#fff',
                        border: 'none', borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 12, fontWeight: 700,
                        cursor: generandoFoto === k.codigo ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                      title="Compone los productos del kit con IA. ~60 seg"
                    >
                      {generandoFoto === k.codigo
                        ? <>⏳ Generando…</>
                        : <>🎨 {k.imagen_url ? 'Regenerar' : 'Generar'} foto IA</>}
                    </button>
                  </div>
                  {/* Preparar dataset para entrenar LoRA propio del kit */}
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={e => prepararDatasetLora(e, k.codigo, k.nombre, k.nItems)}
                      disabled={preparandoLora === k.codigo}
                      style={{
                        width: '100%',
                        background: preparandoLora === k.codigo ? '#94a3b8' : '#fff',
                        color: preparandoLora === k.codigo ? '#fff' : '#1A3461',
                        border: '1px dashed #1A3461', borderRadius: 8,
                        padding: '8px 12px',
                        fontSize: 11, fontWeight: 700,
                        cursor: preparandoLora === k.codigo ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                      title="Genera un ZIP con fotos + captions automáticos listos para entrenar un LoRA propio en Replicate"
                    >
                      {preparandoLora === k.codigo
                        ? <>⏳ Preparando dataset…</>
                        : <>📦 Preparar dataset LoRA</>}
                    </button>
                  </div>
                </Link>
              )
            })}
          </div>
        )}

        {/* Modal con resultado del dataset LoRA */}
        {datasetResult && (
          <div
            onClick={() => setDatasetResult(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,31,61,.6)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 14, maxWidth: 720, width: '100%',
                maxHeight: '90vh', overflow: 'auto', padding: '24px 28px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontSize: 20, fontWeight: 900, color: '#1A3461', margin: 0 }}>📦 Dataset LoRA listo</h2>
                  <p style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                    Kit: <strong>{datasetResult.kitNombre}</strong> · {datasetResult.productos_ok}/{datasetResult.productos_total} productos · {datasetResult.zip_size_kb} KB
                  </p>
                </div>
                <button
                  onClick={() => setDatasetResult(null)}
                  style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
                >×</button>
              </div>
              {datasetResult.productos_fail > 0 && (
                <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#92400E', marginBottom: 12 }}>
                  ⚠ {datasetResult.productos_fail} producto(s) fallaron — revisá el reporte abajo
                </div>
              )}
              <div style={{ background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#475569', textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 800, marginBottom: 6 }}>URL del ZIP (público)</div>
                <input
                  type="text"
                  readOnly
                  value={datasetResult.zip_url}
                  onClick={e => e.target.select()}
                  style={{ width: '100%', fontSize: 12, padding: '8px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontFamily: 'monospace' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <a
                    href={datasetResult.zip_url}
                    download
                    style={{ background: '#1A3461', color: '#fff', padding: '8px 14px', borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}
                  >⬇ Descargar ZIP</a>
                  <button
                    onClick={() => navigator.clipboard.writeText(datasetResult.zip_url)}
                    style={{ background: '#fff', border: '1px solid #CBD5E1', color: '#1A3461', padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >📋 Copiar URL</button>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1A3461', marginBottom: 8 }}>Próximos pasos en Replicate UI</div>
                <ol style={{ fontSize: 13, color: '#475569', lineHeight: 1.7, paddingLeft: 20, margin: 0 }}>
                  {(datasetResult.instrucciones || []).map((paso, i) => (
                    <li key={i}>{paso}</li>
                  ))}
                </ol>
                <a
                  href="https://replicate.com/ostris/flux-dev-lora-trainer/train"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-block', marginTop: 10, background: '#00A8B5', color: '#fff', padding: '8px 14px', borderRadius: 8, textDecoration: 'none', fontSize: 12, fontWeight: 700 }}
                >Abrir Replicate trainer ↗</a>
              </div>
              <details>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#475569' }}>Ver reporte completo ({datasetResult.reporte?.length || 0} productos)</summary>
                <div style={{ marginTop: 10, maxHeight: 300, overflow: 'auto', border: '1px solid #E2E8F0', borderRadius: 8 }}>
                  {(datasetResult.reporte || []).map((r, i) => (
                    <div key={i} style={{ padding: '8px 10px', borderBottom: '1px solid #F1F5F9', fontSize: 11 }}>
                      <div style={{ fontWeight: 700, color: '#1A3461' }}>{r.archivo} · {r.nombre} <span style={{ color: '#94a3b8' }}>({r.sku || '—'})</span></div>
                      <div style={{ color: r.caption?.startsWith('ERROR') ? '#DC2626' : '#475569', marginTop: 2 }}>{r.caption}</div>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
